import { Lightning, Router } from '@lightningjs/sdk'

import { getColor, grey50 } from '../../../constants/colors'
import { UniNeueBold } from '../../../constants/fonts'
import { AppConfig } from '../../../services/appConfig'
import { LinkModelStylesEnum, TvCardStyle, TvCardType } from '../../../types/flo-enums'
import {
  MessageModel,
  TvGridContainerItem,
  TvSearchLayoutModel,
} from '../../../types/flo-experience'
import { getCardSize, PAGINATION_OFFSET } from '../../../utils/carouselUtils'
import { createGridRows, toRowRef, toSearchRowComponent } from '../../../utils/gridUtils'
import { replaceQueryParam } from '../../../utils/misc'
import { Loader } from '../../Loader/LoaderWrapper'
import { PaginateBlock } from '../../PaginateBlock/PaginateBlock'
import { PaginationErrorMessage } from '../../PartialErrorMessage/PaginationErrorMessage'
import { injectPageNumber } from '../../TvGridContainer/TvGridContainer'
import TabMenu from '../TabMenu/TabMenu'
import TvSearchLayout from '../TvSearchLayout'
import { TvSearchGridRow } from './TvSearchGridRow'

export enum SearchGridStates {
  Grid = 'Grid',
  Tabs = 'Tabs',
}

const HEADER_HEIGHT = 101

export default class SearchGrid extends Lightning.Component {
  private _data: TvSearchLayoutModel
  private _totalRows = 0
  private _currentRowIndex = 0
  private _currentOffset = 0
  private _isEmpty = false
  private _isSwitchingTab = false
  private _isCardFadeInEnabled = true
  private _cardType: TvCardType
  private _cardStyle: TvCardStyle
  private _cardSize: { h: number; w: number }
  private _cols: number
  private _hasPagination = false
  private _paginationError = false
  private _items: TvGridContainerItem[] = []

  private _pageNumber = 0
  private _pageSize = 0
  private _paginationUrl = ''
  private _rowsPerPage = 0
  private _previousPage = 0
  private _isPagePending = false
  private _rowHeight = 0
  private _isNextPagePending = false
  private _sectionText = ''

  static _template() {
    return {
      w: (w) => w,
      h: (h) => h,
      Loader: {
        type: Loader,
        x: (w) => w / 2,
        y: (h) => h / 2,
        mount: 0.5,
        width: 112,
        height: 102,
        visible: false,
      },
      Results: {
        w: (w) => w,
        h: (h) => h,
        flex: { direction: 'column' },
        Header: {
          w: (w) => w,
          h: HEADER_HEIGHT,
          Title: {
            y: (h) => h / 2,
            mountY: 0.5,
            zIndex: 3,
            visible: false,
            text: {
              text: '',
              fontSize: 32,
              fontFace: UniNeueBold,
              textColor: getColor(grey50),
            },
          },
          TabMenu: {
            w: 1228,
            zIndex: 3,
            visible: false,
            type: TabMenu,
          },
        },
        Container: {
          w: (w) => w,
          zIndex: 1,
          flex: { direction: 'column' },
        },
      },
    }
  }

  get results() {
    return this.tag('Results')
  }

  get loader() {
    return this.tag('Loader')
  }

  get container() {
    return this.tag('Container')
  }

  get header() {
    return this.tag('Header')
  }

  get title() {
    return this.tag('Title')
  }

  get tabMenu() {
    return this.tag('TabMenu') as TabMenu
  }

  get data() {
    return this._data
  }

  get sectionText(): string {
    return this._sectionText
  }

  set sectionText(v: string) {
    this._sectionText = v
  }

  set data(v) {
    this._data = v
    this._isEmpty = false

    this.renderHeader(v)

    this.renderGridRows(v?.content?.items)
  }

  get currentRow() {
    return this.container.childList.getByRef(toRowRef(this._currentRowIndex)) as TvSearchGridRow
  }

  get isFirstRowFocused() {
    return this._currentRowIndex === 0
  }

  get isLastRowFocused() {
    return this._currentRowIndex === this._totalRows - 1
  }

  get lastOffset() {
    return this._currentOffset ?? 0
  }

  get lastRowIndex() {
    return this._currentRowIndex ?? 0
  }

  get lastCardIndex() {
    return this.currentRow?.currentCardIndex ?? 0
  }

  get lastTabIndex() {
    return this.tabMenu.tabIndex
  }

  set currentOffset(v: number) {
    this._currentOffset = v
    this.container.y = v
  }

  set currentRowIndex(v: number) {
    this._currentRowIndex = v

    if (this.previousPageTrigger) {
      this.loadPreviousPage()
    }

    if (this.nextPageTrigger) {
      if (this._pageNumber === 0) {
        this.loadNextPage()
      } else {
        this.restoreNextPage()
      }
    }
  }

  get currentRowIndex() {
    return this._currentRowIndex
  }

  set currentCardIndex(v: number) {
    if (!this.isEmpty) {
      this.currentRow.currentCardIndex = v
    }
  }

  get isEmpty() {
    return this._isEmpty
  }

  set isCardFadeInEnabled(isEnabled: boolean) {
    this._isCardFadeInEnabled = isEnabled
  }

  get items() {
    const pageNumber = Math.floor(this._currentRowIndex / this._rowsPerPage)

    const currentPageItems = this._items.filter((item: any) => item.page === pageNumber)

    return currentPageItems
  }

  set pageSize(v: number) {
    this._pageSize = v
  }

  get pageNumber() {
    return Math.floor(this._currentRowIndex / this._rowsPerPage)
  }

  set pageNumber(v) {
    this._pageNumber = v
  }

  set paginationUrl(v) {
    this._paginationUrl = v
  }

  get shouldLoadPreviousPage() {
    return this._currentRowIndex === this._rowsPerPage * this._previousPage + 1
  }

  get nextPageTrigger() {
    return this._currentRowIndex % this._rowsPerPage === this._rowsPerPage - 1
  }

  get previousPageTrigger() {
    return this._currentRowIndex % this._rowsPerPage === 0
  }

  get paginationUrl() {
    return this._paginationUrl
  }

  get tvSearchLayout() {
    return this.parent.parent.parent as TvSearchLayout
  }

  get selectedIndex() {
    return this.currentRowIndex * this._cols + this.currentRow.currentCardIndex
  }

  static _states() {
    return [
      class Grid extends this {
        _getFocused() {
          return this.currentRow
        }
      },

      class Tabs extends this {
        _getFocused() {
          return this.tabMenu
        }

        _handleDown() {
          if (this.tvSearchLayout.isErrorSet) {
            this.tvSearchLayout._setState('Error')
          }

          if (!this.isEmpty && !this._isSwitchingTab && !this.tvSearchLayout.isErrorSet) {
            this._setState('Grid')
          }
        }
      },
    ]
  }

  _init() {
    this._setState('Grid')
  }

  _handleUp() {
    this.handleScroll({ deltaY: -1 })
  }

  _handleDown() {
    this.handleScroll({ deltaY: 1 })
  }

  loadingResults(isSearching: boolean) {
    this.results.visible = !isSearching
    this.loader.visible = isSearching
    this.fireAncestors('$nextPageLoadComplete')
  }

  switchingTabs(isSwitchingTab: boolean) {
    this.container.visible = !isSwitchingTab
    this.loader.visible = isSwitchingTab
    this._isSwitchingTab = isSwitchingTab
    this.fireAncestors('$nextPageLoadComplete')
  }

  renderHeader(data: TvSearchLayoutModel) {
    this._sectionText = ''
    if (data?.tab_menu && !data?.content?.title) {
      this.tabMenu.data = data.tab_menu
      this.tabMenu.visible = true
      this.title.visible = false
    }

    const currentRoute = Router.getActiveHash()

    if (data?.content?.title) {
      this._sectionText = data?.content?.title?.text
      this.title.patch({
        text: {
          text: data?.content?.title?.text ?? '',
          fontSize: currentRoute === 'search' ? 32 : 48,
        },
      })
      this.title.visible = true
      this.tabMenu.visible = false
    }
  }

  renderGridRows(items: TvGridContainerItem[]) {
    // Check grid has items before rendering
    this.checkGridItems(items)

    if (this._isEmpty) {
      return
    }

    this._items = injectPageNumber(items, this._pageNumber)

    // Make sure final coordinates are correct before calculating
    // https://lightningjs.io/docs/#/lightning-core-reference/Templates/Flexbox?id=final-coordinates
    this.stage.update()

    this._cardType = items?.[0]?.type as TvCardType
    this._cardStyle = items?.[0]?.style as TvCardStyle
    this._cardSize = getCardSize(this._cardType, this._cardStyle)
    this._rowHeight = this._cardSize?.h

    this._cols = Math.floor(this.finalW / this._cardSize.w)
    this._rowsPerPage = Math.floor(this._pageSize / this._cols)
    this._previousPage = this._pageNumber

    const { rows: totalRows, hasPagination } = createGridRows(
      items,
      this._cols,
      this._pageSize,
      this._pageNumber,
    )
    this._hasPagination = hasPagination

    this._totalRows = totalRows.length - (this._hasPagination ? 1 : 0)

    const children = totalRows.map((row: TvGridContainerItem[], index) =>
      toSearchRowComponent(
        row,
        this._cardSize,
        this._cols,
        index,
        this._isCardFadeInEnabled,
        this._cardStyle,
      ),
    )

    this.container.patch({
      children,
    })
  }

  handleScroll({ deltaY }) {
    const isGoingDown = deltaY > 0

    if (
      (!isGoingDown && this.isFirstRowFocused) ||
      (!isGoingDown &&
        this.currentRow instanceof PaginationErrorMessage &&
        this.currentRow.isPreviousPageError)
    ) {
      if (this.data?.tab_menu) {
        this._setState(SearchGridStates.Tabs)
      }

      return
    }

    if (isGoingDown && this.isLastRowFocused) {
      return
    }

    if (!isGoingDown && this._isPagePending) return

    if (!isGoingDown && this.shouldLoadPreviousPage) {
      this.loadPreviousPage()
    }

    const prevRow = this.currentRow
    this._currentRowIndex += isGoingDown ? 1 : -1
    const nextRow = this.currentRow

    nextRow.currentCardIndex =
      prevRow.currentCardIndex > nextRow.totalCards - 1
        ? nextRow.totalCards - 1
        : prevRow.currentCardIndex

    const curRowOffset = this.currentRow.finalY
    const curRowHeight = this.currentRow.finalH
    const containerCenter = this.finalH / 2

    if (this.isFirstRowFocused) {
      this._currentOffset = 0
    } else if (this.isLastRowFocused) {
      const newYAxis = curRowOffset - this.finalH + curRowHeight
      this._currentOffset = -newYAxis - this.finalY - (this._hasPagination ? PAGINATION_OFFSET : 0)

      if (this._hasPagination && !this._paginationError) {
        this.loadNextPage()
      }
    } else {
      const newYAxis = curRowOffset - containerCenter + curRowHeight / 2
      this._currentOffset = -newYAxis - this.finalY
    }

    this.scroll(this._currentOffset)
  }

  scroll(v: number) {
    this.container.setSmooth('y', v, {
      duration: 0.2,
    })
  }

  reset() {
    this._totalRows = 0
    this._currentRowIndex = 0
    this._pageNumber = 0

    this.container.childList.clear()
    this.scroll((this._currentOffset = 0))
    this._refocus()
  }

  checkGridItems(items: TvGridContainerItem[]) {
    // We get this message item from the grid response as an actual grid item
    // it is used to display the no results message but since it's part of the grid
    // we need to check if it is the only available one
    if (items.length === 1 && items?.[0]?.type === 'message') {
      this._isEmpty = true
      const msgText = (items?.[0] as MessageModel)?.text

      const message = {
        Message: {
          text: {
            text: msgText ?? '',
            fontSize: 48,
            fontFace: UniNeueBold,
            textColor: getColor(grey50),
          },
        },
      }

      this.container.patch({
        children: message,
      })

      return
    }

    this._isEmpty = false
  }

  async loadPreviousPage() {
    this._isPagePending = true

    if (this._previousPage > 0) {
      const url = replaceQueryParam(
        this._paginationUrl,
        'offset',
        (this._previousPage - 1) * this._pageSize,
      )

      const page = await AppConfig.shared.experienceApi.getSearchPartial(url, true)

      if (page?.error) {
        this._isPagePending = false
        const errorIdx = this._currentRowIndex - 1
        const error = {
          h: this._rowHeight,
          type: PaginationErrorMessage,
          isPreviousPageError: true,
          ref: toRowRef(errorIdx),
          retryAction: () => {
            this._currentRowIndex = errorIdx + 1
            this._isPagePending = true
            this.scrollToPreviousRow()
            this.loadPreviousPage()
          },
        }

        this.container.childList.replace(error, this.container.children[this._currentRowIndex - 1])
        return
      }

      const items = page?.data?.items ?? []

      const { rows } = createGridRows(items, this._cols)

      const startIndex = (this._previousPage - 1) * Math.trunc(this._rowsPerPage)

      const children = rows.map((row: TvGridContainerItem[], index) =>
        toSearchRowComponent(
          row,
          this._cardSize,
          this._cols,
          startIndex + index,
          this._isCardFadeInEnabled,
          this._cardStyle,
        ),
      )

      for (let i = 0; i < children.length; i++) {
        this.container.childList.replace(children[i], this.container.children[startIndex + i])
      }

      this._previousPage--
      this._items.unshift(...injectPageNumber(items, this._previousPage))
    }

    this._isPagePending = false
  }

  async loadNextPage(pageBlock?: PaginateBlock) {
    if (this._isNextPagePending) return

    const paginationLink = pageBlock ?? (this.container.childList.last as PaginateBlock)

    if (paginationLink?.style === LinkModelStylesEnum.Paginate && paginationLink?.url) {
      this._isNextPagePending = true
      this._paginationUrl = paginationLink.url

      paginationLink.toggleLoader(true)
      const page = await AppConfig.shared.experienceApi.getSearchPartial(paginationLink.url)
      const items = page?.data?.items

      if (items?.length > 0) {
        this.loadPage({ items, isPaginateBlock: true, removeLast: true })
      } else if (items?.length === 0) {
        this._hasPagination = false
        paginationLink.toggleLoader(false)
        this.container.childList.remove(this.container.childList.last)
        this.scroll(this._currentOffset + PAGINATION_OFFSET)
      } else if (page?.error) {
        const error = {
          type: PaginationErrorMessage,
          ref: toRowRef(this._totalRows),
          retryAction: () => {
            this._currentRowIndex = this._totalRows - 2
            this._paginationError = false
            this._totalRows--
            this.loadNextPage(paginationLink)
          },
        }

        this.container.childList.replace(error, this.container.childList.last)
        this._totalRows++
        this._paginationError = true
      }

      this._isNextPagePending = false
    }
  }

  async restoreNextPage() {
    const url = replaceQueryParam(
      this._paginationUrl,
      'offset',
      (this._pageNumber + 1) * this._pageSize,
    )

    const page = await AppConfig.shared.experienceApi.getSearchPartial(url)

    const items = page?.data?.items ?? []

    if (items && items.length > 0) {
      this.loadPage({ items })
    }
  }

  async loadPage({ items = [], isPaginateBlock = false, removeLast = false }) {
    this._hasPagination = false

    const startIndex = isPaginateBlock ? this._totalRows : this._currentRowIndex + 1

    const { rows, hasPagination } = createGridRows(items, this._cols)

    const children = rows.map((row: TvGridContainerItem[], index) =>
      toSearchRowComponent(
        row,
        this._cardSize,
        this._cols,
        startIndex + index,
        this._isCardFadeInEnabled,
        this._cardStyle,
      ),
    )

    if (removeLast) this.container.childList.remove(this.container.childList.last)

    this.container.childList.a(children)
    this._hasPagination = hasPagination
    this._totalRows += children.length - (this._hasPagination ? 1 : 0)
    this._pageNumber++

    const newItems = injectPageNumber(items, this._pageNumber)
    if (isPaginateBlock) {
      this._items.splice(-1, 1, ...newItems)
    } else {
      this._items = [...this._items, ...newItems]
    }

    this.fireAncestors('$nextPageLoadComplete')
  }

  scrollToPreviousRow() {
    const curRowOffset = this.currentRow.finalY
    const curRowHeight = this.currentRow.finalH
    const containerCenter = this.finalH / 2

    const newYAxis = curRowOffset - containerCenter + curRowHeight / 2
    this._currentOffset = -newYAxis - this.finalY
    this.scroll(this._currentOffset)
  }
}
